import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  Chip,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  styled,
  InputAdornment,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,       
  InputLabel,        
  Select,            
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
  ListItemSecondaryAction as MuiListItemSecondaryAction,
  ListItemText as MuiListItemText,
  Container
} from '@mui/material';
import {
  Search as SearchIcon,
  Report,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ArrowCircleUpOutlined as ArrowCircleUpOutlinedIcon,
  ArrowCircleDownOutlined as ArrowCircleDownOutlinedIcon,
  ArrowCircleUpTwoTone as ArrowCircleUpTwoToneIcon,
  ArrowCircleDownTwoTone as ArrowCircleDownTwoToneIcon
} from '@mui/icons-material';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import ViewTracker from '../components/ViewTracker.tsx';
import LeftPanel from '../components/LeftPanel.tsx';
import { useThemeContext } from '../contexts/ThemeContext.tsx';
import { Particles } from '../components/Particles.tsx';

// --- STÍLUSOK ---

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode, theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(255, 215, 0, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: 'white',
    padding: theme.spacing(1),
  },
  '& .MuiDialogTitle-root': {
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: '1.4rem',
  },
  '& .MuiDialogContent-root': {
    color: 'rgba(255, 255, 255, 0.9)',
  }
}));


const DialogActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 20px',
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: darkMode ? '0 0 15px rgba(255, 64, 129, 0.4)' : '0 0 15px rgba(255, 215, 0, 0.4)',
  }
}));

const Learning: React.FC = () => {
  const isAuthenticated = !!Cookies.get('token');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { darkMode } = useThemeContext();

  const [searchResults, setSearchResults] = useState<any[]>(
    JSON.parse(localStorage.getItem('searchResults') || '[]')
  );
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [reportComment, setReportComment] = useState('');
  const [reportError, setReportError] = useState('');
  const [searchInput, setSearchInput] = useState(
    location.state?.searchInput || localStorage.getItem('searchInput') || ''
  );
  const [sortOption, setSortOption] = useState(
    localStorage.getItem('sortOption') || 'viewsDesc'
  );
  const [formData, setFormData] = useState({ title: '', category: '', shortDescription: '', url: '' });
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [userRatings, setUserRatings] = useState<{ [submissionId: number]: number }>({});
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  
  const isVgpPage = location.pathname.includes('/linklearning');
  const marginLeft = isSmallScreen ? 0 : (leftPanelOpen ? 240 : 56);

  useEffect(() => {
    if (location.state?.searchInput) {
      setSearchInput(location.state.searchInput);
      localStorage.setItem('searchInput', location.state.searchInput);
      if (!location.state.results) {
        handleSearch(new Event('submit') as unknown as React.FormEvent);
      }
    }
    if (location.state?.results) {
      const filtered = location.state.results.filter((item: any) => item.status === 'APPROVED');
      setSearchResults(filtered);
      localStorage.setItem('searchResults', JSON.stringify(filtered));
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    localStorage.setItem('searchInput', value);
    setIsSearchActive(!!value.trim());
    if (!value.trim()) {
      setSearchResults([]);
    }
  };

  const fetchUserRatings = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmyrate`, {
        headers: { 'Authorization': `Bearer ${Cookies.get('token')}` }
      });
      const data = await response.json();
      const ratingsMap: { [submissionId: number]: number } = {};
      data.forEach((item: any) => { ratingsMap[item.submissionId] = item.rate; });
      setUserRatings(ratingsMap);
    } catch (error) {
      console.error("Failed to fetch user ratings:", error);
    }
  };

  const handleSubmitContent = async () => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ ...formData, ...(isPrivate ? { status: "PRIVATE" } : {}) })
      });
      const responseData = await response.json();
      if (response.ok) {
        if (responseData.error) {
          setSubmissionError(responseData.message);
          setSubmissionSuccess(false);
        } else {
          setFormData({ title: '', category: '', shortDescription: '', url: '' });
          setSubmissionSuccess(true);
          setSubmissionError('');
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchInput.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/public/searchitem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searchitem: searchInput })
      });
      const data = await response.json();
      const filtered = data.filter((item: any) => item.status === 'APPROVED');
      setSearchResults(filtered);
      if (isAuthenticated) fetchUserRatings();
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleReport = async () => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/reportcontent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ reason: reportComment, submissionId: selectedContent.id })
      });
      const data = await response.json();
      if (data.message === "You have already reported this content in the last 24 hours.") {
        setReportError(data.message);
      } else {
        setReportDialogOpen(false);
        setReportComment('');
        setReportError('');
      }
    } catch (error) {
      console.error('Report error:', error);
    }
  };

  const handleRate = async (contentId: any, value: 'up' | 'down') => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/ratecontent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ action: value, submissionId: contentId })
      });
      if (response.ok) {
        if (searchInput.trim()) handleSearch(new Event('submit') as unknown as React.FormEvent);
        fetchUserRatings();
      }
    } catch (error) {
      console.error('Rating error:', error);
    }
  };

  const fetchFavorites = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/searchmyfavorites`, {
        headers: { 'Authorization': `Bearer ${Cookies.get('token')}` }
      });
      const data = await response.json();
      setFavorites(data);
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  };

  const handleToggleFavorite = async (submissionId: number) => {
    if (!isAuthenticated) {
      setLoginPromptOpen(true);
      return;
    }
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/v1/content/switchmyfavorites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({
          submissionId: submissionId,
          favorite: favorites.some(fav => fav.id === submissionId) ? 0 : 1
        })
      });
      setFavorites(prev => 
        prev.some(fav => fav.id === submissionId)
          ? prev.filter(fav => fav.id !== submissionId)
          : [...prev, { id: submissionId }]
      );
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  useEffect(() => { fetchFavorites(); }, [isAuthenticated]);
  
  useEffect(() => {
    localStorage.setItem('searchInput', searchInput);
    localStorage.setItem('sortOption', sortOption);
    localStorage.setItem('searchResults', JSON.stringify(searchResults));
  }, [searchInput, sortOption, searchResults]);

  const sortedResults = [...searchResults].sort((a, b) => {
    if (isSearchActive) return 0;
    switch (sortOption) {
      case 'viewsDesc': return b.views - a.views;
      case 'ratedDesc': return (b.up || 0) - (a.up || 0);
      case 'titleAsc': return a.title.localeCompare(b.title);
      case 'titleDesc': return b.title.localeCompare(a.title);
      case 'categoryAsc': return a.category.localeCompare(b.category);
      case 'categoryDesc': return b.category.localeCompare(a.category); 
      default: return b.views - a.views;
    }
  });

  const ContentListItem = ({ result }: { result: any }) => {
    const isFavorite = favorites.some((fav: any) => fav.id === result.id);
    const [bgColor, setBgColor] = useState('transparent');
    const userVote = userRatings[result.id];

    const triggerAnimation = (direction: 'up' | 'down') => {
      setBgColor(direction === 'up' ? 'rgba(0, 255, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)');
      setTimeout(() => setBgColor('transparent'), 1000);
    };

    return (
      <ListItem 
        sx={{ 
          backgroundColor: bgColor, 
          transition: 'background-color 1s ease', 
          display: 'flex', 
          alignItems: 'flex-start',
          pr: { xs: 12, sm: 16 }, 
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <MuiListItemText
          component="div" // Ez javítja ki a validateDOMNesting hibát (p helyett div lesz a konténer)
          primary={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              width: '100%',
              overflow: 'hidden' 
            }}>
              <Typography 
                variant="h6" 
                component="span"
                sx={{ 
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 1,
                  minWidth: 0
                }}
              >
                {result.title}
              </Typography>
              <Chip 
                label={result.category} 
                size="small" 
                sx={{ flexShrink: 0 }}
              />
              <IconButton 
                size="small"
                onClick={() => handleToggleFavorite(result.id)} 
                sx={{ color: isFavorite ? '#ffd700' : 'inherit', flexShrink: 0 }}
              >
                {isFavorite ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            </Box>
          }
          secondary={
            <Box component="div">
              <Typography 
                variant="body2" 
                color="text.secondary" 
                component="div" // Biztosítjuk, hogy itt se legyen p tag
                sx={{ 
                  mt: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {result.shortDescription}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Stack direction="column" alignItems="center">
                  <IconButton onClick={() => { triggerAnimation('up'); setTimeout(() => handleRate(result.id, 'up'), 1000); }} size="small">
                    {userVote === 0 ? <ArrowCircleUpTwoToneIcon fontSize="small" sx={{ color: 'success.main' }} /> : <ArrowCircleUpOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} />}
                  </IconButton>
                  <Typography variant="caption">{result.up || 0}</Typography>
                </Stack>
                <Stack direction="column" alignItems="center">
                  <IconButton onClick={() => { triggerAnimation('down'); setTimeout(() => handleRate(result.id, 'down'), 1000); }} size="small">
                    {userVote === 1 ? <ArrowCircleDownTwoToneIcon fontSize="small" sx={{ color: 'error.main' }} /> : <ArrowCircleDownOutlinedIcon fontSize="small" sx={{ color: 'error.main' }} />}
                  </IconButton>
                  <Typography variant="caption">{result.down || 0}</Typography>
                </Stack>
              </Stack>
            </Box>
          }
          secondaryTypographyProps={{ component: 'div' }} // Másodlagos kényszerítés div-re
        />
        <MuiListItemSecondaryAction sx={{ right: 16, top: 82 }}>
          <Stack spacing={0.5} alignItems="flex-end">
            <ViewTracker 
              contentId={result.id} 
              url={result.url ? (result.url.startsWith('http') ? result.url : `https://${result.url}`) : ''} 
              onViewSuccess={isVgpPage ? () => handleSearch(new Event('submit') as unknown as React.FormEvent) : () => {}}
            />
            <Button 
              size="small" 
              startIcon={<Report />} 
              color="error" 
              sx={{ fontSize: '0.75rem', p: 0 }}
              onClick={() => { if (!isAuthenticated) { setLoginPromptOpen(true); return; } setSelectedContent(result); setReportDialogOpen(true); }}
            >
              Report
            </Button>
            <Tooltip title="Views count increments only when logged-in users click the link">
              <Typography variant="caption" color="text.secondary" sx={{ cursor: isAuthenticated ? 'help' : 'default', fontSize: '0.7rem' }}>
                Views: {result.views.toLocaleString()}
              </Typography>
            </Tooltip>
          </Stack>
        </MuiListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      
      <LeftPanel
        isAuthenticated={isAuthenticated}
        onLoginRequired={() => setLoginPromptOpen(true)}
        onNavigation={(path) => navigate(path)}
        open={leftPanelOpen}
        onToggle={() => setLeftPanelOpen(prev => !prev)}
      />

      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Particles />
      </Box>

      <Box 
          component="main" 
          sx={{ 
            mt: '64px',
            ml: { xs: 0, sm: `${marginLeft}px` },
            transition: 'margin 0.3s ease',
            zIndex: 1,
            flexGrow: 1,
            p: { xs: 2, sm: 4 },
            maxWidth: '100%', 
            overflowX: 'hidden',
            width: { 
              xs: '100%', 
              sm: `calc(100% - ${marginLeft}px)` 
            },
            boxSizing: 'border-box'
          }}
        >
        <Container maxWidth="lg">
          <Box component="form" onSubmit={handleSearch} sx={{ maxWidth: 800, mx: 'auto', mb: 2, pt: 2 }}>
            <TextField
              fullWidth
              value={searchInput}
              onChange={handleInputChange}
              placeholder="Search learning resources..."
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '30px', backgroundColor: theme.palette.background.paper } }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: 800, mx: 'auto' }}>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 1 }}>
              <InputLabel>Sort By</InputLabel>
              <Select 
                value={sortOption} 
                label="Sort By" 
                onChange={(e) => setSortOption(e.target.value)}
              >
                <MenuItem value="viewsDesc">Top Views</MenuItem>
                <MenuItem value="ratedDesc">Top Rated</MenuItem>
                <MenuItem value="titleAsc">Title A-Z</MenuItem>
                <MenuItem value="titleDesc">Title Z-A</MenuItem>
                <MenuItem value="categoryAsc">Category A-Z</MenuItem>
                <MenuItem value="categoryDesc">Category Z-A</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {sortedResults.length > 0 ? (
              <List dense={false}>
                {sortedResults.map((result) => <ContentListItem key={result.id} result={result} />)}
              </List>
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, opacity: 0.7 }}>
                {isSearchActive ? "No matching results found" : "No content available"}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} disableScrollLock>
        <DialogTitle>Report Content</DialogTitle>
        <DialogContent>
          {selectedContent && (
            <>
              <Typography variant="h6">{selectedContent.title}</Typography>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mb: 1, color: reportComment.length > 255 ? 'red' : 'text.secondary' }}>
                {`${reportComment.length}/255`}
              </Typography>
              <TextField fullWidth multiline rows={3} label="Report reason" value={reportComment} onChange={(e) => setReportComment(e.target.value)} />
              {reportError && <Alert severity="warning" sx={{ mt: 2 }}>{reportError}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReport} color="error" variant="contained" disabled={reportComment.length > 255}>Send Report</Button>
        </DialogActions>
      </Dialog>

      <AnimatedDialog open={loginPromptOpen} onClose={() => setLoginPromptOpen(false)} disableScrollLock darkMode={darkMode}>
        <DialogTitle>Authentication Required</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>You need to be logged in to perform this action!</Alert>
          <Stack direction="row" spacing={2} justifyContent="center">
            <DialogActionButton darkMode={darkMode} onClick={() => navigate('/login')}>Sign In</DialogActionButton>
            <DialogActionButton darkMode={darkMode} onClick={() => navigate('/register')}>Sign Up</DialogActionButton>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setLoginPromptOpen(false)} sx={{ color: 'white' }}>Maybe later</Button>
        </DialogActions>
      </AnimatedDialog>

      {/* Create Content Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setSubmissionError(''); }} disableScrollLock>
        <DialogTitle>Submit New Content</DialogTitle>
        <DialogContent>
          {submissionSuccess ? (
            <Button variant="contained" fullWidth onClick={() => { setDialogOpen(false); navigate('/mycontents'); }}>View in My Content</Button>
          ) : (
            <>
              {submissionError && <Alert severity="error" sx={{ mb: 2 }}>{submissionError}</Alert>}
              <TextField autoFocus margin="dense" label="Title" required fullWidth value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
              <TextField margin="dense" label="Category" required fullWidth value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
              <TextField margin="dense" label="Short Description" fullWidth multiline rows={3} value={formData.shortDescription} onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })} />
              <TextField margin="dense" label="URL" required fullWidth value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} />
              <FormControlLabel control={<Switch checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} color="primary" />} label="Private" />
            </>
          )}
        </DialogContent>
        {!submissionSuccess && (
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitContent} disabled={!formData.title || !formData.category || !formData.url} variant="contained">Submit</Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default Learning;